import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Input, List, Typography, Space, Tag } from 'antd';
import PropTypes from 'prop-types';
import { SearchOutlined, DashboardOutlined, DatabaseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import getNavigationConfig from 'configs/NavigationConfig';
import { APP_PREFIX_PATH } from 'configs/AppConfig';
import { useSubscription } from 'contexts/SubscriptionContext';
import { getUserRole } from 'utils/getUserRole';
import { PREMIUM_SUBSCRIPTION_TYPES } from 'constants/PremiumConstant';
import IntlMessage from '../util-components/IntlMessage';
import Icon from '../util-components/Icon';

const { Text } = Typography;
const { Search } = Input;

/**
 * Navigation Search Component
 * Appears on double shift press and allows quick navigation to any menu item
 */
const NavigationSearch = ({ visible, onClose }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentTheme = useSelector(state => state.theme.currentTheme);

  // Get navigation data with proper dependency tracking
  const { subscriptionType, isLoading: subscriptionLoading } = useSubscription();
  const userRole = getUserRole();
  const hasValidSubscription = PREMIUM_SUBSCRIPTION_TYPES.includes(subscriptionType);

  // Get navigation config dynamically - using memoization for performance
  const navigationConfig = useMemo(() => {
    try {
      // Don't generate navigation if subscription is still loading
      if (subscriptionLoading) {
        return [];
      }

      // Use the dynamic navigation config function
      const config = getNavigationConfig(userRole, hasValidSubscription);

      // If config is empty, create a basic navigation structure
      if (!config || config.length === 0) {
        const basicConfig = [
          {
            key: 'workspace',
            path: `${APP_PREFIX_PATH}/workspace`,
            title: 'Workspace',
            icon: DashboardOutlined,
            breadcrumb: false,
            isGroupTitle: true,
            submenu: [
              {
                key: 'dashboards-default',
                path: `${APP_PREFIX_PATH}/dashboards/default`,
                title: 'Dashboard',
                icon: DashboardOutlined,
                breadcrumb: true,
                submenu: []
              },
              {
                key: 'dashboards-search',
                path: `${APP_PREFIX_PATH}/search`,
                title: 'Search',
                icon: SearchOutlined,
                breadcrumb: true,
                submenu: []
              },
              {
                key: 'kb',
                path: `${APP_PREFIX_PATH}/kb`,
                title: 'Knowledge Base',
                icon: DatabaseOutlined,
                breadcrumb: true,
                submenu: []
              },
              {
                key: 'help',
                path: `${APP_PREFIX_PATH}/help`,
                title: 'Help',
                icon: QuestionCircleOutlined,
                breadcrumb: true,
                submenu: []
              }
            ]
          }
        ];
        return basicConfig;
      }

      return config;
    } catch (error) {
      console.error('Error generating navigation config:', error);
      // Return basic fallback even on error
      return [
        {
          key: 'workspace',
          path: `${APP_PREFIX_PATH}/workspace`,
          title: 'Workspace',
          icon: DashboardOutlined,
          breadcrumb: false,
          isGroupTitle: true,
          submenu: [
            {
              key: 'dashboards-default',
              path: `${APP_PREFIX_PATH}/dashboards/default`,
              title: 'Dashboard',
              icon: DashboardOutlined,
              breadcrumb: true,
              submenu: []
            }
          ]
        }
      ];
    }
  }, [userRole, hasValidSubscription, subscriptionType, subscriptionLoading]);

  // Flatten navigation items for search
  const flattenedItems = useMemo(() => {
    const items = [];

    const processItem = (item, parentPath = '') => {
      // Skip group titles but process their children
      if (item.isGroupTitle) {
        if (item.submenu && item.submenu.length > 0) {
          item.submenu.forEach(subItem => processItem(subItem, parentPath));
        }
        return;
      }

      const fullPath = item.path || parentPath;
      const itemData = {
        key: item.key,
        title: item.title,
        path: fullPath,
        icon: item.icon,
        breadcrumb: item.breadcrumb,
        level: parentPath ? 1 : 0
      };

      items.push(itemData);

      // Process submenu items
      if (item.submenu && item.submenu.length > 0) {
        item.submenu.forEach(subItem => processItem(subItem, fullPath));
      }
    };

    // Process main navigation config
    if (navigationConfig && navigationConfig.length > 0) {
      navigationConfig.forEach(item => {
        processItem(item);
      });
    }

    // Set loading to false once items are processed
    setIsLoading(false);

    return items;
  }, [navigationConfig]);

  // Filter items based on search text
  const filteredItems = useMemo(() => {
    if (!searchText.trim()) {
      return flattenedItems;
    }

    const searchLower = searchText.toLowerCase();

    const filtered = flattenedItems.filter(item => {
      const title = typeof item.title === 'string' ? item.title : '';
      const key = item.key || '';
      const path = item.path || '';

      // Create searchable text from all relevant fields
      let searchableText = `${title} ${key} ${path}`.toLowerCase();

      // If title is a localization key, also include the readable part
      if (title.startsWith('sidenav.')) {
        const readableText = title
          .replace('sidenav.dashboard.', '')
          .replace('sidenav.', '')
          .replace(/\./g, ' ');
        searchableText += ` ${readableText}`;
      }

      // Check if search term matches any part of the searchable text
      return searchableText.includes(searchLower);
    });

    return filtered;
  }, [flattenedItems, searchText]);

  // Debug logging (reduced for performance)
  useEffect(() => {
    if (visible) {
      // only comment was there inside these so removed it
    }
  }, [visible, subscriptionType, userRole, hasValidSubscription, flattenedItems.length, filteredItems.length, searchText]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!visible) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < filteredItems.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : filteredItems.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex]) {
            handleItemSelect(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, filteredItems, selectedIndex, onClose]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchText]);

  // Focus search input when modal opens
  useEffect(() => {
    if (visible && searchInputRef.current) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [visible]);

  // Reset search text when modal closes
  useEffect(() => {
    if (!visible) {
      setSearchText('');
    }
  }, [visible]);

  // Handle item selection
  const handleItemSelect = (item) => {
    if (item.path && item.path !== location.pathname) {
      try {
        navigate(item.path);
      } catch (error) {
        console.error('Navigation failed for path:', item.path, error);
        const basePath = item.path.split('/').slice(0, -1).join('/');
        if (basePath) {
          navigate(basePath);
        }
      }
    }
    onClose();
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  // Render navigation item
  const renderNavItem = (item, index) => {
    const isSelected = index === selectedIndex;
    const isActive = item.path === location.pathname;

    // Get display title
    let displayTitle;
    if (typeof item.title === 'string' && item.title.startsWith('sidenav.')) {
      try {
        displayTitle = <IntlMessage id={item.title} />;
      } catch (error) {
        console.error('Failed to render IntlMessage for', item.title, error);
        displayTitle = item.title
          .replace('sidenav.dashboard.', '')
          .replace('sidenav.', '');
      }
    } else {
      displayTitle = item.title;
    }
    const getItemBackground = (isSelected, theme) =>
      isSelected ? (theme === 'dark' ? '#1890ff' : '#e6f7ff') : 'transparent';

    const getTextColor = (isSelected, theme) =>
      isSelected ? (theme === 'dark' ? '#fff' : '#000') : 'inherit';

    return (
      <List.Item
        key={item.key}
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          backgroundColor: getItemBackground(isSelected, currentTheme),
          border: isActive ? '2px solid #1890ff' : '2px solid transparent',
          borderRadius: '6px',
          margin: '2px 0',
          transition: 'all 0.2s ease'
        }}
        onClick={() => handleItemSelect(item)}
      >
        <Space>
          {item.icon && <Icon type={item.icon} />}
          <div style={{ flex: 1 }}>
            <Text
              strong={isActive}
              style={{
                color: getTextColor(isSelected, currentTheme)
              }}
            >
              {displayTitle}
            </Text>
            {item.breadcrumb && (
              <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>
                {item.path}
              </div>
            )}
          </div>
          {isActive && <Tag color="blue" size="small">Current</Tag>}
        </Space>
      </List.Item>
      // <List.Item
      //   key={item.key}
      //   style={{
      //     padding: '12px 16px',
      //     cursor: 'pointer',
      //     backgroundColor: isSelected ? (currentTheme === 'dark' ? '#1890ff' : '#e6f7ff') : 'transparent',
      //     border: isActive ? `2px solid ${currentTheme === 'dark' ? '#1890ff' : '#1890ff'}` : '2px solid transparent',
      //     borderRadius: '6px',
      //     margin: '2px 0',
      //     transition: 'all 0.2s ease'
      //   }}
      //   onClick={() => handleItemSelect(item)}
      // >
      //   <Space>
      //     {item.icon && <Icon type={item.icon} />}
      //     <div style={{ flex: 1 }}>
      //       <Text 
      //         strong={isActive}
      //         style={{ 
      //           color: isSelected ? (currentTheme === 'dark' ? '#fff' : '#000') : 'inherit'
      //         }}
      //       >
      //         {displayTitle}
      //       </Text>
      //       {item.breadcrumb && (
      //         <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>
      //           {item.path}
      //         </div>
      //       )}
      //     </div>
      //     {isActive && (
      //       <Tag color="blue" size="small">Current</Tag>
      //     )}
      //   </Space>
      // </List.Item>
    );
  };

  /**
   * Helper function to render search content (SonarQube fix - extract nested ternary operations)
   */
  const renderSearchContent = () => {
    if (isLoading) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: currentTheme === 'dark' ? '#8c8c8c' : '#666'
        }}>
          <div>Loading navigation items...</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            Subscription: {subscriptionType || 'None'}, Role: {userRole || 'None'}
          </div>
        </div>
      );
    }

    if (filteredItems.length > 0) {
      return (
        <List
          key={`filtered-${searchText}-${filteredItems.length}`}
          dataSource={filteredItems}
          renderItem={renderNavItem}
          size="small"
        />
      );
    }

    if (searchText.trim()) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: currentTheme === 'dark' ? '#8c8c8c' : '#666'
        }}>
          <SearchOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
          <div>No navigation items found for "{searchText}"</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            Try searching for: dashboard, search, agent, knowledge, etc.
          </div>
          <div style={{ fontSize: '11px', marginTop: '8px', color: '#888' }}>
            Available items: {flattenedItems.length}
          </div>
        </div>
      );
    }

    return (
      <div style={{
        textAlign: 'center',
        padding: '40px 20px',
        color: currentTheme === 'dark' ? '#8c8c8c' : '#666'
      }}>
        <SearchOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
        <div>Start typing to search navigation items</div>
        <div style={{ fontSize: '12px', marginTop: '4px' }}>
          Available: {flattenedItems.length} items
        </div>
        <div style={{ fontSize: '11px', marginTop: '4px', color: '#888' }}>
          Role: {userRole || 'None'}, Subscription: {subscriptionType || 'None'}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <SearchOutlined />
          <span>Quick Navigation</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
      maskClosable={true}
      style={{ top: '20%' }}
    >
      <div style={{ marginBottom: '16px' }}>
        <Search
          ref={searchInputRef}
          placeholder="Search navigation items (e.g., dashboard, search, agent, etc.)"
          value={searchText}
          onChange={handleSearchChange}
          size="large"
          autoFocus
          prefix={<SearchOutlined />}
        />
      </div>

      <div style={{
        maxHeight: '400px',
        overflowY: 'auto',
        border: `1px solid ${currentTheme === 'dark' ? '#434343' : '#d9d9d9'}`,
        borderRadius: '6px',
        padding: '8px'
      }}>
        {renderSearchContent()}
      </div>

      <div style={{
        marginTop: '12px',
        padding: '8px 12px',
        backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : '#f5f5f5',
        borderRadius: '4px',
        fontSize: '12px',
        color: currentTheme === 'dark' ? '#8c8c8c' : '#666'
      }}>
        <Text>
          💡 <strong>Tip:</strong> Use ↑↓ arrows to navigate, Enter to select, Esc to close
        </Text>
      </div>
    </Modal>
  );
};
NavigationSearch.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default NavigationSearch; 